/**
 * Hero Component
 *
 * Full-width hero section with background image, property name,
 * subtitle, and embedded contact form.
 * Features: Ken Burns background, staggered entrance animations,
 * bottom gradient fade.
 */

import {
  useContentToken,
  EditableToken,
  useSlotGroup,
  EditableSlotGroup,
  useSlotGroupOverrides,
  useBlockSlot,
  SectionContextProvider,
} from '@vibesites/runtime/react';
import type { SlotGroupDefinition, ComponentContentSchema } from '@vibesites/runtime';
import { useFormattedRichText } from './useFormattedRichText';

const RANGE_RE = /^\s*(.+?)\s*(?:[-–—]|\bto\b)\s*(.+?)\s*$/i;

/** Collapse a degenerate bedroom range like "4-4" to "4". */
function collapseBedroomRange(property: Record<string, any>): Record<string, any> {
  const range = property.property_bedrooms_range;
  if (typeof range !== 'string') return property;
  const match = range.match(RANGE_RE);
  if (!match || match[1].toLowerCase() !== match[2].toLowerCase()) return property;
  return { ...property, property_bedrooms_range: match[1] };
}

const heroImageDefinition: SlotGroupDefinition = {
  label: 'Hero Background',
  fields: {
    image: {
      label: 'Background Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Property exterior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: {
      image: 'image',
      alt: 'alt_text',
    },
  },
};

export function Hero() {
  const slotGroupOverrides = useSlotGroupOverrides();

  const propertyName = useContentToken('hero.property_name');
  const subtitle = useFormattedRichText('hero.subtitle', collapseBedroomRange, 'h2');

  const formSlot = useBlockSlot('hero.form_block', {
    defaultValue: 'multihub-lead-form',
  });

  const heroImage = useSlotGroup('hero.background', heroImageDefinition);

  return (
    <section className="relative min-h-[90vh] lg:min-h-[90vh] md:min-h-auto bg-primary flex items-center overflow-hidden">
      {/* Background image with Ken Burns effect */}
      {heroImage.image && (
        <div
          className="absolute inset-0 bg-cover bg-center animate-[kenBurns_25s_ease-in-out_infinite_alternate]"
          style={{ backgroundImage: `url(${heroImage.image})` }}
        />
      )}

      {/* Background overlay */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Bottom gradient fade — uses neutral black to avoid theme color bleed */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/30 to-transparent z-[2]" />

      <div className="relative z-[3] w-full max-w-[1400px] mx-auto px-4 py-8 md:px-6 md:py-12 lg:px-6 lg:py-16 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center">
        {/* Left content */}
        <div className="text-white">
          <EditableSlotGroup
            groupPath="hero.background"
            definition={heroImageDefinition}
            override={slotGroupOverrides['hero.background']}
            style={{ position: 'absolute', top: '1rem', left: '1rem', zIndex: 10 }}
          >
            <span className="hidden">Edit Background Image</span>
          </EditableSlotGroup>

          <div className="animate-[fadeInUp_0.8s_ease-out_both]">
            <EditableToken path="hero.property_name" type="text" label="Property Name" as="div">
              <h1 className="text-[clamp(2rem,5vw,4rem)] font-bold font-heading mb-4 [text-shadow:0_2px_10px_rgba(0,0,0,0.3)] leading-[1.1]">
                {propertyName}
              </h1>
            </EditableToken>
          </div>

          <div className="animate-[fadeInUp_0.8s_ease-out_0.2s_both]">
            <EditableToken
              path="hero.subtitle"
              type="richtext"
              label="Hero Subtitle"
              as={subtitle.tag as any}
              className="text-[clamp(1rem,2.5vw,1.75rem)] font-normal font-body [text-shadow:0_1px_5px_rgba(0,0,0,0.3)] leading-relaxed first-letter:uppercase"
            >
              <span data-vibesite-richtext="" dangerouslySetInnerHTML={{ __html: subtitle.html }} style={{ display: 'contents' }} />
            </EditableToken>
          </div>
        </div>

        {/* Form area */}
        <div className="flex justify-end lg:justify-end justify-center animate-[fadeInUp_0.8s_ease-out_0.4s_both] scroll-mt-20 -mx-[10px] lg:mx-0" id="contact">
          <EditableToken
            path="hero.form_block"
            type="block"
            label="Form Block"
            as="div"
            style={{ width: '100%', maxWidth: '500px' }}
          >
            {formSlot ? (
              <div className="mh-form-host backdrop-blur-sm bg-white/5 rounded-2xl p-1.5">
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              </div>
            ) : (
              <div className="bg-white/10 rounded-lg p-12 px-8 text-center text-white/60 border-2 border-dashed border-white/20">
                <p>No form block selected</p>
                <p className="text-sm opacity-70">
                  Select a block in the editor sidebar
                </p>
              </div>
            )}
          </EditableToken>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes kenBurns {
          from { transform: scale(1); }
          to { transform: scale(1.08); }
        }
        /* Smaller field type so two-up date/bedroom row doesn't clip placeholders */
        .mh-form-host input,
        .mh-form-host select,
        .mh-form-host textarea {
          font-size: 0.8125rem !important;
        }
        .mh-form-host input[name="move_in_date_act"] + span {
          font-size: 0.8125rem !important;
        }
        /* Custom select chevron: strip native arrow, inject SVG with controlled inset */
        .mh-form-host select {
          -webkit-appearance: none !important;
          appearance: none !important;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8' fill='none' stroke='%239ca3af' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M1 1l5 5 5-5'/%3E%3C/svg%3E") !important;
          background-repeat: no-repeat !important;
          background-position: right 1rem center !important;
          background-size: 12px 8px !important;
          padding-right: 2.5rem !important;
        }
        @media (max-width: 480px) {
          /* Tighter card padding on small screens */
          .mh-form-host > div { padding: 1.25rem !important; }
          /* Stack first/last name inputs vertically (mirrors date/bedrooms vendor rule) */
          .mh-form-host form > div:first-child { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero',
  tokens: {
    'hero.property_name': {
      label: 'Property Name',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Welcome Home',
    },
    'hero.subtitle': {
      label: 'Hero Subtitle',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_bedrooms_range"></span> Bedroom Apartments in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
    'hero.form_block': {
      label: 'Form Block',
      type: 'block',
      description: 'The block component to render in the hero form area',
      defaultValue: 'multihub-lead-form',
    },
  },
  slotGroups: {
    'hero.background': heroImageDefinition,
  },
};

(Hero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Hero;
